import React, {useEffect, useState} from 'react';
import {Box} from '@mui/material';
import styles from './preferences.module.scss';
import {
    getEndDayTime,
    getStartDayTime,
    getTimeZone, updateStartDayTime, updateEndDayTime,
    updateTimeZone
} from '../../../../features/settings/preferences';
import {useAppDispatch, useAppSelector} from '../../../../hooks/hooks';
import {useIntl} from 'react-intl';
import {dayTimes, timeZones} from './types';
import { useMediaQuery } from 'react-responsive';
import { Dropdown, Option } from '../../../../components/shared/nextgen/dropdown';
import { DropdownWrapper } from '../../../../components/shared/nextgen/dropdown-wrapper';
import TabLayout from '../../../../components/shared/tab-layout';
import SettingsHeader from '../settings-header';
import { canManageJobExecutionTime, canManageTimezone } from '../../../../features/profile/entityPermissions';
import { NAVIGATION_ROUTES } from '../../../../routes/NavigationRoute';
import { useNavigate } from 'react-router-dom';
import { isSectionVisible } from '../../../../helpers/permissionHelper';
import { NAVIGATION_SECTION_VISIBILITY } from '../../../../features/profile/navigationVisibility';

export default function Preferences() {
    const intl = useIntl();
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const profile = useAppSelector(store => store.profile);
    const preferences = useAppSelector(store => store.preferences);
    const [timezoneLoading, setTimezoneLoading] = useState(true);
    const [startdaytimeLoading, setStartdaytimeLoading] = useState(true);
    const [enddaytimeLoading, setEnddaytimeLoading] = useState(true);
    const isMob = useMediaQuery({ maxWidth: 1000 });
    const canEditTimezone = canManageTimezone(profile);
    const canEditJobExecutionTime = canManageJobExecutionTime(profile);
    const canSeePreferences = isSectionVisible(profile, NAVIGATION_SECTION_VISIBILITY.settingsPreferences);
    const loading = (canEditTimezone && timezoneLoading)
        || (canEditJobExecutionTime && startdaytimeLoading)
        || (canEditJobExecutionTime && enddaytimeLoading);

    const getTwelveTimeIfNeeded = (time: string, enabled: boolean): string => {
        if (!enabled) {
            return time;
        }

        let hours = parseInt(time.split(':')[0]);
        const amOrPm = hours >= 12 ? 'PM' : 'AM';
        hours = (hours % 12) || 12;
        return `${hours}:00 ${amOrPm}`;
    }

    const dayTimesOptions = dayTimes.map((value) => ({
        id: value.id,
        label: getTwelveTimeIfNeeded(value.time, false)
    }));

    const handleTimeZoneChange = (timeZone: number) => {
        if (timeZone === preferences.timezone){
            return;
        }
        dispatch(updateTimeZone(timeZone)).unwrap().then(() => {
            dispatch(getTimeZone());
        })
    }

    const handleStartDayTimeChange = (dayTime: number) => {
        const dayTimeWithTicks = `${dayTime}:00`;
        if (dayTimeWithTicks === preferences.startdaytime){
            return;
        }
        dispatch(updateStartDayTime(dayTimeWithTicks)).unwrap().then(() => {
            dispatch(getStartDayTime());
        })
    }

    const handleEndDayTimeChange = (dayTime: number) => {
        const dayTimeWithTicks = `${dayTime}:00`;
        if (dayTimeWithTicks === preferences.enddaytime){
            return;
        }
        dispatch(updateEndDayTime(dayTimeWithTicks)).unwrap().then(() => {
            dispatch(getEndDayTime());
        })
    }

    const findIdOfDayTime = (value: string): number => {
        return  dayTimes.find(dayTime => `${dayTime.time}:00` === value)!.id;
    }

    useEffect(() => {
        if (!canSeePreferences) {
            navigate(NAVIGATION_ROUTES.SETTINGS, { replace: true });
            return;
        }

        setTimezoneLoading(true);
        setStartdaytimeLoading(true);
        setEnddaytimeLoading(true);

        if (canEditTimezone) {
            dispatch(getTimeZone()).finally(() => {
                setTimezoneLoading(false);
            });
        } else {
            setTimezoneLoading(false);
        }

        if (canEditJobExecutionTime) {
            dispatch(getStartDayTime()).finally(() => {
                setStartdaytimeLoading(false);
            });
            dispatch(getEndDayTime()).finally(() => {
                setEnddaytimeLoading(false);
            });
        } else {
            setStartdaytimeLoading(false);
            setEnddaytimeLoading(false);
        }
    }, [canEditJobExecutionTime, canEditTimezone, canSeePreferences, dispatch, navigate]);

    if (!canSeePreferences) {
        return null;
    }

    const renderLabel = (id: string, label: string) => (
        <label htmlFor={id} className={styles.fieldLabel}>
            {label}
        </label>
    );

    return (
        <TabLayout header={<SettingsHeader activeTab={'preferences'} loading={loading} />}>
            <Box className={styles.formBody} sx={{ padding: isMob ? '12px' : '0 32px 16px' }}>
                <div style={{ width: '100%', maxWidth: isMob ? '100%' : '250px', display: 'flex', flexDirection: 'column', gap: '24px' }}>
                    { 
                        canEditTimezone && !timezoneLoading && (
                            <div className={styles.fieldGroup}>
                                {renderLabel('preferences-timezone', intl.formatMessage({ id: 'time_zone' }))}
                                <Dropdown
                                    name='timezone'
                                    value={preferences.timezone}
                                    placeholder={intl.formatMessage({ id: 'time_zone' })}
                                    onChange={(_, value) => handleTimeZoneChange(value as number)}>
                                    {timeZones.map(option => (
                                        <Option key={option.id} value={option.id}>
                                            {intl.formatMessage({ id: option.id >= 0 ? `time_z${option.id}` : `time_zw${Math.abs(option.id)}` })}
                                        </Option>
                                    ))}
                                </Dropdown>
                            </div>
                        )
                    }
                    { 
                        canEditJobExecutionTime && !startdaytimeLoading && (
                            <div className={styles.fieldGroup}>
                                {renderLabel('preferences-startdaytime', intl.formatMessage({ id: 'start_day_time' }))}
                                <DropdownWrapper
                                    name='startdaytime'
                                    value={findIdOfDayTime(preferences.startdaytime)}
                                    placeholder={intl.formatMessage({ id: 'start_day_time' })}
                                    options={dayTimesOptions}
                                    onChange={(_, value) => handleStartDayTimeChange(value as number)}
                                />
                            </div>
                        )
                    }
                    { 
                        canEditJobExecutionTime && !enddaytimeLoading && (
                            <div className={styles.fieldGroup}>
                                {renderLabel('preferences-enddaytime', intl.formatMessage({ id: 'end_day_time' }))}
                                <DropdownWrapper
                                    name='enddaytime'
                                    value={findIdOfDayTime(preferences.enddaytime)}
                                    placeholder={intl.formatMessage({ id: 'end_day_time' })}
                                    options={dayTimesOptions}
                                    onChange={(_, value) => handleEndDayTimeChange(value as number)}
                                />
                            </div>
                        )
                    }
                </div> 
            </Box>
        </TabLayout>
    );
}
